import {CloseOutlined, LogoutOutlined, RightOutlined, UserOutlined} from '@ant-design/icons';
import {Drawer} from 'antd';
import cn from 'classnames';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';
import {Link, useLocation} from 'react-router-dom';

import * as localizationKeys from '../../consts/localization/localizationKeys';
import {PROFILE_PAGE} from '../../consts/route/routePaths';
import useMenuScrollContainment from '../../hooks/useMenuScrollContainment';
import appService from '../../services/app/appService';
import {getLocalizedStrings} from '../../services/localization/localizationService';
import {makeSelectLogin} from '../../state/selectors/auth';
import Logo from '../Logo/Logo';
import menuService from './menuService';
import PwaInstallMenuItem from './PwaInstallMenuItem';

const MobileMenu = ({isOpen, items, activeItem, onItemClick, onClose}) => {
    const [navigationNode, setNavigationNode] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const {pathname} = useLocation();
    const login = useSelector(makeSelectLogin());
    const localizedStrings = getLocalizedStrings();
    const isProfileActive = pathname === PROFILE_PAGE;

    useMenuScrollContainment(navigationNode, isOpen || isVisible);

    const handleAfterOpenChange = (opened) => {
        setIsVisible(opened);
        if (!opened) return;

        // This Ant Design version does not forward an accessible name to its dialog.
        navigationNode?.closest('[role="dialog"]')?.setAttribute('aria-labelledby', 'mobile-navigation-title');
    };

    const handleLogout = () => {
        onClose();
        appService.logout();
    };

    return (
        <Drawer
            open={isOpen}
            autoFocus={false}
            onClose={onClose}
            afterOpenChange={handleAfterOpenChange}
            placement='top'
            height='100%'
            zIndex={1100}
            rootClassName='mobile-navigation-drawer'
            closable={false}
            title={
                <div className='mobile-navigation-brand'>
                    <Logo />
                    <span id='mobile-navigation-title'>Energy Portal</span>
                </div>
            }
            extra={
                <button type='button' className='mobile-navigation-close' aria-label='Закрити меню' onClick={onClose}>
                    <CloseOutlined aria-hidden />
                </button>
            }
            footer={
                <div className='mobile-navigation-footer'>
                    <PwaInstallMenuItem onMobileMenuClose={onClose} mobile />
                    <span className='mobile-navigation-version'>Версія 2.3.0</span>
                </div>
            }
        >
            <nav
                id='mobile-navigation'
                ref={setNavigationNode}
                aria-label='Energy Portal'
                className='mobile-navigation'
            >
                <ul className='mobile-navigation-list'>
                    {items.map((item) => {
                        const isActive = item.type === activeItem;

                        return (
                            <li key={item.type}>
                                <Link
                                    to={item.href}
                                    onClick={() => onItemClick(item)}
                                    aria-current={isActive ? 'page' : undefined}
                                    className={cn('mobile-navigation-link', {
                                        'mobile-navigation-link-active': isActive,
                                    })}
                                >
                                    <span className='mobile-navigation-icon' aria-hidden>
                                        {menuService.getMenuIcons(item.type, isActive)}
                                    </span>
                                    <span className='mobile-navigation-label'>{item.value}</span>
                                    <RightOutlined className='mobile-navigation-arrow' aria-hidden />
                                </Link>
                            </li>
                        );
                    })}
                </ul>
                <ul className='mobile-navigation-list mobile-navigation-account'>
                    <li>
                        <Link
                            to={PROFILE_PAGE}
                            onClick={onClose}
                            aria-current={isProfileActive ? 'page' : undefined}
                            className={cn('mobile-navigation-link', {'mobile-navigation-link-active': isProfileActive})}
                        >
                            <span className='mobile-navigation-icon' aria-hidden>
                                <UserOutlined />
                            </span>
                            <span className='mobile-navigation-label'>
                                {localizedStrings[localizationKeys.USER_MENU_PROFILE_ITEM]}
                                {login && <span className='mobile-navigation-account-name'>{login}</span>}
                            </span>
                            <RightOutlined className='mobile-navigation-arrow' aria-hidden />
                        </Link>
                    </li>
                    <li>
                        <button
                            type='button'
                            className='mobile-navigation-link mobile-navigation-link-logout'
                            onClick={handleLogout}
                        >
                            <span className='mobile-navigation-icon' aria-hidden>
                                <LogoutOutlined />
                            </span>
                            <span className='mobile-navigation-label'>
                                {localizedStrings[localizationKeys.USER_MENU_LOGOUT_ITEM]}
                            </span>
                        </button>
                    </li>
                </ul>
            </nav>
        </Drawer>
    );
};

export default MobileMenu;
